//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import moment from 'moment-timezone';

import { PurchaseOption } from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { useGetSamplingOpportunitiesQuery } from '@campaigns/components/campaign-requests/sampling-campaigns/SamplingCampaignsQueries';

interface UsePurchaseOptionsParams {
  readOnly?: boolean;
  selectedOptions?: PurchaseOption[];
}

interface UsePurchaseOptionsResult {
  error: Error | undefined;
  isPending: boolean;
  purchaseOptions: PurchaseOption[];
}

/**
 * Hook that fetches and filters purchase options based on the mode (readonly vs editable).
 * - Filters out options with 0 or negative balance (for both modes)
 * - In readonly mode: filters out all expired options
 * - In editable mode: filters out expired options UNLESS they are currently selected
 */
export const usePurchaseOptions = ({
  readOnly = false,
  selectedOptions = [],
}: UsePurchaseOptionsParams): UsePurchaseOptionsResult => {
  const { data, error, isPending } = useGetSamplingOpportunitiesQuery();

  // With the V2 update (applied to standard files), data.opportunities are already PurchaseOption[]
  const purchaseOptions: PurchaseOption[] = data?.opportunities ?? [];

  const filteredPurchaseOptions = purchaseOptions.filter((option) => {
    // Filter out options with 0 or negative balance
    const hasPositiveBalance = (option.balance ?? 0) > 0;
    if (!hasPositiveBalance) {
      return false;
    }

    const isExpired = moment(option.expiryDate).isBefore(moment());

    if (!isExpired) {
      return true; // Always show non-expired options
    }

    if (readOnly) {
      return false; // Never show expired options in readonly mode
    }

    // In editable mode, show expired options only if they are selected
    const isSelected = selectedOptions.some(
      (selected) => selected.opportunityId === option.opportunityId,
    );
    return isSelected;
  });

  return {
    error: error ?? undefined,
    isPending,
    purchaseOptions: filteredPurchaseOptions,
  };
};
